import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from '../apiClient';

export type IncidentResolution = {
  id: string;
  createdAt: string;
  updatedAt: string;
  projectId: string;
  incidentId: string;

  status: 'in_progress' | 'completed' | 'failed' | 'timeout';
  rootCause?: string;
  confidence?: number;
  steps: IncidentResolutionStep[];
};

export type IncidentResolutionStepType =
  | 'acknowledged'
  | 'user_message'
  | 'ai_answer'
  | 'ai_answer_failure';

export type IncidentResolutionStep = {
  id: string;
  createdAt: string;
  updatedAt: string;
  incidentResolutionId: string;
  projectId: string;

  type: IncidentResolutionStepType;
  message?: string;
};

export const rcaIncidentResolutionsQueries = {
  all: () => ['rca', 'incident-resolutions'],
  byProject: (projectId: string) => [
    ...rcaIncidentResolutionsQueries.all(),
    projectId,
  ],
  byIncident: (projectId: string, incidentId: string) => [
    ...rcaIncidentResolutionsQueries.byProject(projectId),
    incidentId,
  ],
  list: (projectId: string, incidentId: string) =>
    queryOptions({
      queryKey: [
        ...rcaIncidentResolutionsQueries.byIncident(projectId, incidentId),
        'list',
      ],
      queryFn: async () => {
        const incidentResolutions = await listIncidentResolutions(
          projectId,
          incidentId,
        );

        return incidentResolutions;
      },
    }),
} as const;

const listIncidentResolutions = async (
  projectId: string,
  incidentId: string,
): Promise<IncidentResolution[]> => {
  const getResponse = await fetchBackend(
    `/rca/${projectId}/incidents/${incidentId}/resolutions`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    incidentResolutions: IncidentResolution[];
  };

  return data.incidentResolutions;
};
